import {
   AppointmentPractitioner,
   AvailabilitySimple,
   AvailabilitySimpleQuery,
   HealthcareProviderLocationIdentifier,
   PractitionerAbleToCompleteAppointmentQuery,
} from '@dreryk/egabinet-appointment-frontend-api';
import {
   HealthcareProviderLocation,
   HealthcareProviderLocationQuery,
} from '@dreryk/egabinet-healthcare-provider-frontend-api';
import { PatientSimple, PatientSimpleQuery } from '@dreryk/egabinet-patient-frontend-api';
import { PractitionerId } from '@dreryk/egabinet-personnel-published-language-api';
import { ReceptionServiceId } from '@dreryk/egabinet-private-medical-service-published-language-api';
import {
   ReceptionServiceForPractitionerSimple,
   ReceptionServiceForPractitionerSimpleQuery,
} from '@dreryk/egabinet-private-medical-service-frontend-api';

import { useCqrsGet, useCqrsSearch } from '@dreryk/command-query-react-common';
import { TimetableSlotSelectionEvent } from '@dreryk/react-calendar';
import { LoaderSpinner, Message, MessageType } from '@dreryk/react-components';
import { Range, Slot } from '@dreryk/watermelon';
import { paginations } from '@dreryk/command-query-ws-client';

import { appointmentsTranslationKeys } from '../../../../translations';

// eslint-disable-next-line @typescript-eslint/no-unnecessary-type-constraint
const allEqual = <T extends unknown>(array: T[]): boolean => {
   const jsonStrings = array.map((element) => JSON.stringify(element));
   return new Set(jsonStrings).size === 1;
};

const getHealthcareProviderLocationIdentifierFromScheduleIfSingle = (
   availabilities: AvailabilitySimple[]
): HealthcareProviderLocationIdentifier | null => {
   const locations = availabilities
      .map((availability) => availability.location)
      .filter((value): value is HealthcareProviderLocationIdentifier => Boolean(value));

   if (!locations?.length) {
      return null;
   }

   if (allEqual(locations)) {
      return locations[0];
   }

   return null;
};

type AppointmentCreateFormLoaderRenderer = (result: {
   practitioners: AppointmentPractitioner[];
   patient: PatientSimple;
   healthcareProviderLocations: HealthcareProviderLocation[];
   healthcareProviderLocationIdentifierFromSchedule: HealthcareProviderLocationIdentifier | null;
   receptionService: ReceptionServiceForPractitionerSimple | undefined;
}) => JSX.Element;

type AppointmentWaitlistCreateFormLoaderProps = {
   readonly selectedSlot: TimetableSlotSelectionEvent<AppointmentPractitioner>;
   readonly children: AppointmentCreateFormLoaderRenderer;
   readonly patientId: number;
   readonly receptionServiceId: ReceptionServiceId | undefined;
   readonly practitionerId: PractitionerId;
};

// eslint-disable-next-line max-lines-per-function
export const AppointmentWaitlistCreateFormLoader = (props: AppointmentWaitlistCreateFormLoaderProps): JSX.Element => {
   const { data: patient, loading: patientLoading, error: patientError } = useCqrsGet<PatientSimple>(() => {
      const query = new PatientSimpleQuery();
      query.id = Slot.of(props.patientId);
      return query;
   });

   const {
      data: receptionServices,
      loading: receptionServicesLoading,
      error: receptionServicesError,
   } = useCqrsSearch<ReceptionServiceForPractitionerSimple>(
      () => {
         const query = new ReceptionServiceForPractitionerSimpleQuery();
         query.practitionerId = props.practitionerId.asNumber();
         return query;
      },
      paginations.all(),
      { enabled: Boolean(props.receptionServiceId) }
   );

   const {
      data: practitioners,
      loading: practitionersLoading,
      error: practitionersError,
   } = useCqrsSearch<AppointmentPractitioner>(() => new PractitionerAbleToCompleteAppointmentQuery());

   const {
      data: healthcareProviderLocations,
      loading: healthcareProviderLocationsLoading,
      error: healthcareProviderLocationsError,
   } = useCqrsSearch<HealthcareProviderLocation>(() => new HealthcareProviderLocationQuery());

   const {
      data: availabilities,
      loading: availabilitiesLoading,
      error: availabilitiesError,
   } = useCqrsSearch<AvailabilitySimple>(() => {
      const query = new AvailabilitySimpleQuery();
      query.dateRange = Range.closedOpen(props.selectedSlot.start, props.selectedSlot.end);
      query.practitionerIds = [props.selectedSlot.resource.id];
      return query;
   });

   if (
      practitionersLoading ||
      healthcareProviderLocationsLoading ||
      availabilitiesLoading ||
      patientLoading ||
      receptionServicesLoading
   ) {
      return <LoaderSpinner />;
   }

   if (
      practitionersError ||
      healthcareProviderLocationsError ||
      availabilitiesError ||
      patientError ||
      receptionServicesError
   ) {
      return (
         <Message
            testId={'appointmentWaitlistCreateDataFetchError'}
            type={MessageType.NEGATIVE}
            label={appointmentsTranslationKeys.create.form.errors.fetchData}
            displayIndicator
         />
      );
   }

   return props.children({
      receptionService: receptionServices?.find(
         (receptionService) => receptionService.receptionServiceId.asNumber() === props.receptionServiceId?.asNumber()
      ),
      patient: patient,
      practitioners: practitioners,
      healthcareProviderLocations: healthcareProviderLocations,
      healthcareProviderLocationIdentifierFromSchedule: getHealthcareProviderLocationIdentifierFromScheduleIfSingle(
         availabilities
      ),
   });
};
